import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

import { InitialPageComponent } from './initial-page/initial-page.component';
import { SetupPageComponent } from './setup-page/setup-page.component';
import { NationalSetupPageComponent } from './national-setup-page/national-setup-page.component';
import { FactoryGuiComponent } from './factory-gui/factory-gui.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { HelpPageComponent } from '@efsShared/common-components/help-page/help-page.component';
import { SpecialLinksPageComponent } from '@efsShared/common-components/special-links-page/special-links-page.component';
import { CommonListCorpsComponent } from '@efsShared/common-components/common-list-corps/common-list-corps.component';
import { CommonListNationalCorpsComponent } from './shared/common-components/common-list-national-corps/common-list-national-corps.component';
import { RuleSetupListCorpsComponent } from './shared/common-components/common-list-corps/ruleSetup-list-corps.component';
import { SingleUserSetupICCComponent } from './single-user-setup-icc/single-user-setup-icc.component';
import { MailerIdSetupComponent } from './mailer-id-setup/mailer-id-setup.component';
import { ParameterEntryComponent } from './parameter-entry/parameter-entry.component';
import { CorpProfileSetupComponent } from './corp-profile-setup/corp-profile-setup.component';
import { PerfMeasurementsComponent } from './perf-measurements/perf-measurements.component';
import { AuthGuard } from '@EfsReusable/auth/auth.guard';

const routes: Routes = [
  { path: '', redirectTo: '/efs', pathMatch: 'full' },
  { path: 'efs', component: InitialPageComponent, data: { breadcrumb: 'EFS' } },
  {
    path: 'corpSlaExportReport', loadChildren: () => import('./corp-sla-export-report/corp-sla-export-report.module').then(m => m.CorpSlaExportReportModule),
  },
  {
    path: 'setup', component: SetupPageComponent, data: { breadcrumb: 'Setup' },
    children: [
      { path: 'mailerIdSetup', component: MailerIdSetupComponent, data: { breadcrumb: 'Mailer ID Setup' } },
      { path: 'parameterEntry', component: ParameterEntryComponent, data: { breadcrumb: 'Parameter Entry' } },
      { path: 'perfMeasurements', component: PerfMeasurementsComponent, data: { breadcrumb: 'Perf Measurements' }, canActivate: [AuthGuard], },
      { path: 'corpProfileSetup', component: CorpProfileSetupComponent, data: { breadcrumb: 'Corp Profile Setup' } },
      {
        path: 'searchSpecialInstructions', loadChildren:
          () => import(
            './special-instructions/special-instructions.module').then(
              m => m.SpecialInstructionsModule), data: { breadcrumb: 'Special Instructions' }
      },
      {
        path: 'prehold', canActivate: [AuthGuard], loadChildren: () => import(
          './prehold/prehold.module').then(m => m.PreholdModule), data: { breadcrumb: 'Prehold' }
      },
      {
        path: 'delayedMail', canActivate: [AuthGuard],
        loadChildren: () => import(
          './delayed-mail/delayed-mail.module').then(m => m.DelayedMailModule), data: { breadcrumb: 'Delayed Mail' }
      },
      {
        path: 'productionSetup', canActivate: [AuthGuard],
        loadChildren: () => import(
          './production-setup/production-setup.module').then(m => m.ProductionSetupModule), data: { breadcrumb: 'Production Setup' }
      },
      {
        path: 'productionMethod', canActivate: [AuthGuard],
        loadChildren: () => import('./production-method-group/production-method-group.module').then(m => m.ProductionMethodGroupModule),
        data: { breadcrumb: 'Production Method Group' }
      },
      {
        path: 'planTemplate', canActivate: [AuthGuard],
        loadChildren: () => import('./plan-template/plan-template.module').then(m => m.PlanTemplateModule),
        data: { breadcrumb: 'Plan Template' }
      },
      {
        path: 'packageTemplate', canActivate: [AuthGuard],
        loadChildren: () => import('./package-template/package-template.module').then(m => m.PackageTemplateModule),
        data: { breadcrumb: 'Package Template' }
      },
      {
        path: 'corpSetup', canActivate: [AuthGuard],
        loadChildren: () => import('./corp-setup/corp-setup.module').then(m => m.CorpSetupModule),
        data: { breadcrumb: 'Corp Setup' }
      },
      {
        path: 'ruleSetup', canActivate: [AuthGuard],
        loadChildren: () => import('./rule-setup/rule-setup.module').then(m => m.RuleSetupModule),
        data: { breadcrumb: 'Rule Setup' }
      }
    ]
  },
  {
    path: 'nationalSetup', component: NationalSetupPageComponent, data: { breadcrumb: 'National Setup' },
    children: [
      {
        path: 'nationalCorpSetup', canActivate: [AuthGuard],
        loadChildren: () => import('./national-corp-setup/national-corp-setup.module').then(m => m.NationalCorpSetupModule),
        data: { breadcrumb: 'National Corp Setup' }
      },
      {
        path: 'nationalPackageTemplate', canActivate: [AuthGuard],
        loadChildren: () => import(
          './national-package-template/national-package-template.module').then(m => m.NationalPackageTemplateModule),
        data: { breadcrumb: 'National Package Template' }
      },
      {
        path: 'nationalDispatchTemplate', canActivate: [AuthGuard],
        loadChildren: () => import(
          './national-dispatch-template/national-dispatch-template.module').then(m => m.NationalDispatchTemplateModule),
        data: { breadcrumb: 'National Dispatch Template' }
      }
    ]
  },
  {
    path: 'clientSummary', canActivate: [AuthGuard],
    loadChildren: () => import('./national-dashboard/national-dashboard.module').then(m => m.NationalDashboardModule),
    data: { breadcrumb: 'National Dashboard/Client Summary' }
  },
  { path: 'admin', loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule) },
  {
    path: 'listCorps', // canActivate: [AuthGuard],
    component: CommonListCorpsComponent, data: { breadcrumb: 'List Corps' },
  },
  {
    path: 'listCorpsRS', // canActivate: [AuthGuard],
    component: RuleSetupListCorpsComponent, data: { breadcrumb: 'List Corps' },
  },
  {
    path: 'listNationalCorps', // canActivate: [AuthGuard],
    component: CommonListNationalCorpsComponent, data: { breadcrumb: 'List National Corps' },
  },
  { path: 'helpPage', component: HelpPageComponent, data: { breadcrumb: 'Help Page' } },
  {
    path: 'roleAdmin', canActivate: [AuthGuard],
    loadChildren: () => import('./role-admin/role-admin.module').then(m => m.RoleAdminModule),
    data: { breadcrumb: 'Role Admin' }
  },
  { path: 'factoryServices', canActivate: [AuthGuard], component: FactoryGuiComponent, data: { breadcrumb: 'Factory Services' } },
  { path: 'singleUserSetupICC', component: SingleUserSetupICCComponent, data: { breadcrumb: 'Single User Setup ICC' } },
  {
    path: 'cmFub21kc2RwZmFzd3dzZG9zYWNkY29laWV3c2Vhc2RmYW5vdGhlcm9uZW5ld3Bhc3NncmVhdGVydGhhbm9yaWdpbmFsc3NvbWV0aGlucGFzc2tpkb21sb25n',
    component: SpecialLinksPageComponent, data: { breadcrumb: 'TEST' }
  },
  {
    path: 'archival-and-retrieval', canActivate: [AuthGuard],
    loadChildren: () => import('./archival-and-retrieval/archival-and-retrieval.module').then(m => m.ArchivalAndRetrievalModule),
    data: { breadcrumb: 'Archival and Retrieval' }
  },
  {
    path: 'mk-bill-of-material', canActivate: [AuthGuard],
    loadChildren: () => import('./mk-bill-of-material/mk-bill-of-material.module').then(m => m.MkBillOfMaterialModule),
    data: { breadcrumb: 'MK Bill of Material' }
  },
  { path: 'agreementsScreen', canActivate: [AuthGuard],
     loadChildren: () => import('./agreements-screen/agreements-screen.module').then(m => m.AgreementsScreenModule),
     data: { breadcrumb: 'Agreements' }
  },
  { path: '**', component: NotFoundComponent }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
